import { useState, useReducer, useContext } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Box, ToggleButtonGroup, ToggleButton, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { Flex, FlexBetween } from '../../../commonStyles';
import { IAssignment } from '../../../types/assignment';
import AssignmentList from './AssignmentList';
import { purchasersReducer, informationReducer } from './reducers';
import { useAppDispatch } from '../../../app/hooks';
import { showSuccessSnackbar, showErrorSnackbar } from '../../../features/snackbar/snackbarSlice';
import Purchasers from '../../common/forms/Purchasers';
import AssignmentInformation from './AssignmentInformation';
import AssignmentRealtor from './AssignmentRealtor';
import { useSelector } from 'react-redux';
import { selectProject } from '../../../features/project/projectSlice';
import { UnitContext } from '../../../context/UnitContext';
import { IPurchaserInfo } from '../../../types/CreateDealForm';
import LoadingLogo from '../../common/LoadingLogo';
import { realtorReducer } from './reducers';
import Owners from './Owners';

const Assignment = () => {
  const storeDispatch = useAppDispatch();
  const { unit } = useContext(UnitContext);
  const project = useSelector(selectProject);
  const [assignments, setAssignments] = useState<IAssignment[]>([]);
  const [assignment, setAssignment] = useState<IAssignment | null>(null);
  const [informationState, informationDispatch] = useReducer(informationReducer, {
    signDate: assignment ? assignment.signDate : null,
    price: assignment ? assignment.price : '',
    current: assignment ? assignment.current : false,
    miscellaneous: assignment ? assignment.miscellaneous : [],
  });
  const [assignorsState, assignorsDispatch] = useReducer(purchasersReducer, assignment && assignment.assignors ? assignment.assignors : []);
  const [assigneesState, assigneesDispatch] = useReducer(
    purchasersReducer,
    assignment && assignment.assignees ? assignment.assignees : []
  );
  const [assignorsRealtorState, assignorsRealtorDispatch] = useReducer(
    realtorReducer,
    assignment && assignment.assignorsRealtor ? assignment.assignorsRealtor : null
  );
  const [assigneesRealtorState, assigneesRealtorDispatch] = useReducer(
    realtorReducer,
    assignment && assignment.assigneesRealtor ? assignment.assigneesRealtor : null
  );
  const [tabValue, setTabValue] = useState<string>(assignment ? 'assignors' : 'current');

  const handleTabChange = (e: any, value: string) => {
    if (value !== null) {
      setTabValue(value);
    }
  };

  const { loading } = useQuery(GETASSIGNMENTS, {
    variables: { filter: { project: project._id, unit: unit._id } },
    onCompleted: (data) => {
      let selectedAssignment = data.assignmentMany.find((assignment: IAssignment) => assignment.current);
      if (selectedAssignment) {
        setTabValue('assignors');
        setData(selectedAssignment);
      }
      setAssignments(data.assignmentMany);
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const setData = (assignment: IAssignment) => {
    assignorsDispatch({
      type: 'SWAP',
      payload: assignment.assignors,
    });
    assigneesDispatch({
      type: 'SWAP',
      payload: assignment.assignees,
    });
    assignorsRealtorDispatch({
      type: 'ADD',
      payload: assignment.assignorsRealtor,
    });
    assigneesRealtorDispatch({
      type: 'ADD',
      payload: assignment.assigneesRealtor,
    });
    informationDispatch({
      type: 'SWAP',
      payload: {
        signDate: assignment.signDate,
        miscellaneous: assignment.miscellaneous,
        price: assignment.price,
        current: assignment.current,
        _id: assignment._id,
      },
    });
    setAssignment(assignment);
  };

  const [createAssignment, { loading: creatingAssignment }] = useMutation(CREATEASSIGNMENT, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Assignment Saved!'));
      setAssignment(data.assignmentCreateOne.record);
      setAssignments([assignments, data.assignmentCreateOne.record]);
      setTabValue('assignors');
      assignorsDispatch({
        type: 'SWAP',
        payload: data.assignmentCreateOne.record.assignors,
      });
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar('Error Saving Assignment'));
      console.log(err, 'err');
    },
  });

  const [updateAssignment] = useMutation(UPDATEASSIGNMENT, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Assignment Saved!'));
      setAssignment(data.assignmentUpdateById.record);
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar('Error Saving Assignment'));
      console.log(err, 'err');
    },
  });

  const [archiveAssignment] = useMutation(UPDATEASSIGNMENT, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Assignment Archived!'));
      setAssignment(null);
      setAssignments([...assignments, data.assignmentUpdateById.record]);
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar('Error Saving Assignment'));
      console.log(err, 'err');
    },
  });

  const handleSave = (id: string, field: string) => {
    if (assignment) {
      let type: any[] = [];
      if (field === 'Assignees') {
        type = assignment.assignees.map((purchaser: IPurchaserInfo) => purchaser._id);
      } else if (field === 'Assignors') {
        type = assignment.assignors.map((purchaser: IPurchaserInfo) => purchaser._id);
      }
      if (!type.includes(id)) {
        updateAssignment({
          variables: {
            _id: assignment._id,
            record: {
              [field.toLocaleLowerCase()]: [...type, id],
            },
          },
        });
      }
    }
  };

  const handleDelete = (id: string, field: string) => {
    if (assignment) {
      if (field === 'assignees' || field === 'assignors') {
        let type: any[] = [];
        if (field === 'assignees') {
          type = assignment.assignees.map((purchaser: IPurchaserInfo) => purchaser._id);
        } else if (field === 'assignors') {
          type = assignment.assignors.map((purchaser: IPurchaserInfo) => purchaser._id);
        }
        updateAssignment({
          variables: {
            _id: assignment._id,
            record: {
              [field]: type.filter((typeId: string) => typeId !== id),
            },
          },
        });
      }
    }
  };

  return (
    <div>
      <FlexBetween>
        <Flex>
          {tabValue === 'previous' ? (
            <ArrowBackIcon
              sx={{ cursor: 'pointer', mr: 1, alignSelf: 'center' }}
              onClick={() => {
                setTabValue('current');
                setAssignment(null);
              }}
            />
          ) : null}
          <h2>Assignment</h2>
        </Flex>
        {assignment ? (
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                archiveAssignment({
                  variables: {
                    _id: assignment._id,
                    record: {
                      current: false,
                    },
                  },
                });
                setTabValue('current');
              }}
            >
              Archive
            </Button>
          </Box>
        ) : null}
      </FlexBetween>
      {!loading ? (
        <>
          <FlexBetween>
            {assignments.length ? (
              <ToggleButtonGroup
                value={tabValue}
                exclusive
                onChange={handleTabChange}
                aria-label="text alignment"
                sx={{
                  '& .MuiToggleButton-root.Mui-selected': {
                    backgroundColor: '#2e453d',
                    color: '#fff',
                  },
                  '& .MuiToggleButton-root.Mui-selected:hover': {
                    backgroundColor: '#2e453d',
                    color: '#fff',
                  },
                }}
              >
                {assignment ? (
                  <ToggleButton value="assignors" aria-label="right aligned">
                    Assignor
                  </ToggleButton>
                ) : null}
                {assignment ? (
                  <ToggleButton value="assignorsRealtor" aria-label="centered">
                    Assignor's Realtor
                  </ToggleButton>
                ) : null}
                {assignment ? (
                  <ToggleButton value="assignees" aria-label="centered">
                    Assignee
                  </ToggleButton>
                ) : null}
                {assignment ? (
                  <ToggleButton value="assigneesRealtor" aria-label="centered">
                    Assignee's Realtor
                  </ToggleButton>
                ) : null}
                {assignment ? (
                  <ToggleButton value="documents" aria-label="right aligned">
                    Miscellaneous
                  </ToggleButton>
                ) : null}
                <ToggleButton value="previous" aria-label="right aligned">
                  Past Assignments
                </ToggleButton>
              </ToggleButtonGroup>
            ) : null}
          </FlexBetween>
          {!assignment ? (
            <Owners
              createAssignment={createAssignment}
              creating={creatingAssignment}
              assignment={null}
              handleSave={handleSave}
              handleDelete={handleDelete}
            />
          ) : null}
          {tabValue === 'assignors' && assignment ? (
            <Box sx={{ mt: 2 }}>
              <Purchasers
                title="Assignors"
                purchasersState={assignorsState}
                dispatch={assignorsDispatch}
                handleSave={handleSave}
                handleDelete={handleDelete}
                formType='assignment'
              />
            </Box>
          ) : null}
          {tabValue === 'assignorsRealtor' && assignment ? (
            <AssignmentRealtor
              title={"Assignors Realtor"}
              realtor={assignorsRealtorState}
              realtorDispatch={assignorsRealtorDispatch}
              handleSave={handleSave}
              assignment={assignment}
            />
          ) : null}
          {tabValue === 'assignees' && assignment ? (
            <Box sx={{ mt: 2 }}>
              <Purchasers
                title="Assignees"
                purchasersState={assigneesState}
                dispatch={assigneesDispatch}
                handleSave={handleSave}
                handleDelete={handleDelete}
                formType='assignment'
              />
            </Box>
          ) : null}
          {tabValue === 'assigneesRealtor' && assignment ? (
            <AssignmentRealtor
              title={"Assignees Realtor"}
              realtor={assigneesRealtorState}
              realtorDispatch={assigneesRealtorDispatch}
              handleSave={handleSave}
              assignment={assignment}
            />
          ) : null}
          {tabValue === 'documents' ? (
            <Box sx={{ mt: 2 }}>
              <AssignmentInformation
                assignment={assignment}
                information={informationState}
                informationDispatch={informationDispatch}
                updateAssignment={updateAssignment}
              />
            </Box>
          ) : null}
          {tabValue === 'previous' ? (
            assignments.length ? (
              <AssignmentList assignments={assignments} setAssignment={setData} />
            ) : (
              <Box sx={{ mt: 2 }}>
                <em>There are no previous assignments</em>
              </Box>
            )
          ) : null}
        </>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
          }}
        >
          <LoadingLogo />
        </Box>
      )}
    </div>
  );
};

const UPDATEASSIGNMENT = gql`
  mutation assignmentUpdateById($_id: MongoID!, $record: UpdateByIdAssignmentInput!) {
    assignmentUpdateById(_id: $_id, record: $record) {
      record {
        _id
        project {
          _id
        }
        unit {
          _id
        }
        assignees {
          _id
          email
          firstName
          lastName
          corp
          sin
          dob
          identifications {
            _id
            name
            getUrl
          }
          unit
          streetAddress
          city
          province
          country
          postalCode
          occupation
          employer
          directors
          businessNumber
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          idJurisdiction
          getUrl
          putUrl
        }
        assigneesRealtor {
          _id
          realtor {
            _id
          }
          email
          firstName
          lastName
          brokerage
          streetAddress
          city
          province
          country
          postalCode
          brokeragePhone
          brokerageFax
          directPhone
          coopRates {
            type
            days
            date
            description
            percentage
            fixedAmount
          }
        }
        assignors {
          _id
          email
          firstName
          lastName
          corp
          sin
          dob
          identifications {
            _id
            name
            getUrl
          }
          unit
          streetAddress
          city
          province
          country
          postalCode
          occupation
          employer
          directors
          businessNumber
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          idJurisdiction
          getUrl
          putUrl
        }
        assignorsRealtor {
          _id
          realtor {
            _id
          }
          email
          firstName
          lastName
          brokerage
          streetAddress
          city
          province
          country
          postalCode
          brokeragePhone
          brokerageFax
          directPhone
          coopRates {
            type
            days
            date
            description
            percentage
            fixedAmount
          }
        }
        price
        signDate
        miscellaneous {
          _id
          getUrl
          name
        }
        deposits {
          _id
        }
        current
      }
    }
  }
`;

const CREATEASSIGNMENT = gql`
  mutation assignmentCreateOne($record: CreateOneAssignmentInput!) {
    assignmentCreateOne(record: $record) {
      record {
        _id
        project {
          _id
        }
        unit {
          _id
        }
        assignees {
          _id
          email
          firstName
          lastName
          corp
          sin
          dob
          identifications {
            _id
            name
            getUrl
          }
          unit
          streetAddress
          city
          province
          country
          postalCode
          occupation
          employer
          directors
          businessNumber
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          idJurisdiction
          getUrl
          putUrl
        }
        assigneesRealtor {
          _id
          realtor {
            _id
          }
          email
          firstName
          lastName
          brokerage
          streetAddress
          city
          province
          country
          postalCode
          brokeragePhone
          brokerageFax
          directPhone
          coopRates {
            type
            days
            date
            description
            percentage
            fixedAmount
          }
        }
        assignors {
          _id
          email
          firstName
          lastName
          corp
          sin
          dob
          identifications {
            _id
            name
            getUrl
          }
          unit
          streetAddress
          city
          province
          country
          postalCode
          occupation
          employer
          directors
          businessNumber
          signingOfficers {
            fullName
            dob
            sin
            primaryPhone
            streetAddress
            email
          }
          purchaserType
          primaryPhone
          idType
          idNumber
          idExpiry
          idJurisdiction
          getUrl
          putUrl
        }
        assignorsRealtor {
          _id
          realtor {
            _id
          }
          email
          firstName
          lastName
          brokerage
          streetAddress
          city
          province
          country
          postalCode
          brokeragePhone
          brokerageFax
          directPhone
          coopRates {
            type
            days
            date
            description
            percentage
            fixedAmount
          }
        }
        price
        signDate
        miscellaneous {
          _id
          getUrl
          name
        }
        deposits {
          _id
        }
        current
      }
    }
  }
`;

const GETASSIGNMENTS = gql`
  query assignmentMany($filter: FilterFindManyAssignmentInput) {
    assignmentMany(filter: $filter) {
      _id
      project {
        _id
      }
      unit {
        _id
      }
      assignees {
        _id
        project {
          _id
        }
        email
        firstName
        lastName
        corp
        sin
        dob
        identifications {
          _id
          name
          getUrl
        }
        unit
        streetAddress
        city
        province
        country
        postalCode
        occupation
        employer
        directors
        businessNumber
        signingOfficers {
          fullName
          dob
          sin
          primaryPhone
          streetAddress
          email
        }
        purchaserType
        primaryPhone
        idType
        idNumber
        idExpiry
        idJurisdiction
        getUrl
        putUrl
      }
      assigneesRealtor {
        _id
        email
        firstName
        lastName
        brokerage
        streetAddress
        city
        province
        country
        postalCode
        brokeragePhone
        brokerageFax
        directPhone
        coopRates {
          type
          days
          date
          description
          percentage
          fixedAmount
        }
      }
      assignors {
        _id
        project {
          _id
        }
        email
        firstName
        lastName
        corp
        sin
        dob
        identifications {
          _id
          name
          getUrl
        }
        unit
        streetAddress
        city
        province
        country
        postalCode
        occupation
        employer
        directors
        businessNumber
        signingOfficers {
          fullName
          dob
          sin
          primaryPhone
          streetAddress
          email
        }
        purchaserType
        primaryPhone
        idType
        idNumber
        idExpiry
        idJurisdiction
        getUrl
        putUrl
      }
      assignorsRealtor {
        _id
        email
        firstName
        lastName
        brokerage
        streetAddress
        city
        province
        country
        postalCode
        brokeragePhone
        brokerageFax
        directPhone
        coopRates {
          type
          days
          date
          description
          percentage
          fixedAmount
        }
      }
      price
      signDate
      miscellaneous {
        _id
        getUrl
        name
      }
      deposits {
        _id
      }
      current
    }
  }
`;

export default Assignment;
